<template>
    <div class="sensors" :class="[{ detail: sensorSelected }, $mq]">
        <template v-if="sensorSelected && $route.name == 'SensorDetail'">
            <SensorDetail @filterDate="filterDetail" :filtersActive="currentFilters" :sensorID="sensorSelected" @close="back" />
        </template>
        <template v-else>
            <div class="header-wrapper">
                <div class="header-top">
                    <div class="tabs-container">
                        <div class="tab" :class="{ active: activeTab === 'sensors' }" @click="switchTab('sensors')">
                            <i class="i-andy-receptor i-andy-m"></i>
                            <span class="tab-text">{{ $t('tools.sensor_title') }}</span>
                        </div>
                        <div class="tab" :class="{ active: activeTab === 'notifications' }" @click="switchTab('notifications')">
                            <i class="i-andy-circle-exclamation i-andy-m"></i>
                            <span class="tab-text">{{ $t('config.dashboard.notify_history') }} ({{ notificationCount }})</span>
                        </div>
                    </div>
                    <div class="subtitle">{{ $t('sensor.last_24_hours') }}</div>
                    <div class="header-right">
                        <div class="refresh-button" @click="load()">
                            <i class="i-andy-refresh-2 i-andy-m"></i>
                            <span class="refresh-text">{{ $t('sensor.refresh') }}</span>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Sensors Tab Content -->
            <SensorsView v-if="activeTab === 'sensors'" :sensors="sensors" :priority-sensors="prioritySensors" :kpi-data="kpiData" :is-sensors-loaded="isSensorsLoaded" :conection="conection" :activeKpiType="activeKpiType" @kpi-click="handleKpiClick" @sensor-select="selectSensor" @reload="load" />

            <!-- Notifications Tab Content -->
            <NotificationsView v-else :notifications="formattedNotifications" :kpi-data="notificationKpiData" :active-filter="notificationFilter" @filter-change="handleNotificationKpiClick" @notification-select="selectSensorFromNotification" />
        </template>
    </div>
</template>

<script>
import Tabs from '../components/domain/ui/Tabs.vue'
import EmptyPage from '../components/layout/EmptyPage.vue'
import ViewCharts from '../components/domain/sensor/viewCharts.vue'
import i18n from '@/i18n'
import { isMutedSensor } from '@/utils/sensorStatus'
import SensorDetail from '@/components/domain/sensor/sensorDetail.vue'

import SensorsView from '@/components/domain/sensor/SensorsView.vue'
import NotificationsView from '@/components/domain/sensor/NotificationsView.vue'
import chartSensor from '@/components/domain/sensor/chartSensor.vue'
import VirtualList from 'vue-virtual-scroll-list'

export default {
    components: {
        chartSensor,
        SensorDetail,
        SensorsView,
        NotificationsView,
        VirtualList,
        ViewCharts,
        EmptyPage,
        Tabs
    },
    name: 'Sensors',
    data() {
        return {
            chartComponent: chartSensor,
            activeTab: 'sensors',
            sensorCharts: true,
            allIssuesSelected: true,
            activeKpiType: null,
            sensorSelected: false,
            key: 0,
            page: 1,
            perPage: 20,
            total: false,
            totalAlertsPaginated: [],
            openIssuesPaginated: [],
            pageOpenIssues: 1,
            perPageOpenIssues: 20,
            totalOpenIssues: false,
            openIssuesList: [],
            maxDate: true,
            minDate: true,
            showButtonFilter: false,
            currentFilters: {
                type_date: 'today',
                date: {
                    start_date: false,
                    end_date: false
                }
            },
            emptyFilters: {
                type_date: 'today',
                date: {
                    start_date: false,
                    end_date: false
                }
            },
            printCharts: false,
            cssImages: css_image_url,
            isSensorsLoaded: false,
            conection: true,
            notificationFilter: null // null = all, 'issues' = incidents only, 'alerts' = alerts only
        }
    },
    computed: {
        checkConnetion() {
            return this.$store.getters['getConnState']
        },
        optionsDate() {
            return [
                { value: 'today', name: i18n.t('assets.date.today_last_hours') },
                { value: 'yesterday', name: i18n.t('assets.date.yesterday') }
            ]
        },
        sensors() {
            let sensors = Object.values(this.$store.getters['sensor/getList']())

            const now = Date.now()
            const twentyFourHoursAgo = now - 24 * 60 * 60 * 1000

            return sensors.filter((sensor) => {
                if (sensor.status <= 0) return false
                if (sensor.status === 1) return true

                if (sensor.last_value && sensor.last_value.date) {
                    const sensorDate = sensor.last_value.date * 1000
                    return sensorDate >= twentyFourHoursAgo
                }

                return false
            })
        },
        issueStates() {
            return this.$store.getters['getIssueStates']
        },
        sensorAlerts() {
            return this.$store.getters['sensor/getSensorValues']()
        },
        sensorsWithAlerts() {
            if (this.sensorAlerts && Object.values(this.sensorAlerts).length > 0) {
                let sensors = Object.values(this.sensorAlerts)

                sensors = sensors.filter((sensor) => {
                    return Object.values(sensor.values).length > 0
                })

                let aSensor = []
                sensors.forEach((sensor) => {
                    for (let index in sensor.values) {
                        if (sensor.values[index].alert) {
                            const timestamp = parseInt(index)

                            // Determine if it's an incident or just an alert
                            const hasIssue = sensor.values[index].issue && sensor.values[index].issue.issue_id
                            const isOpen = hasIssue && sensor.values[index].issue.status !== 3 // status 3 = closed

                            let cardClass = 'alert-card'
                            if (hasIssue) {
                                cardClass = isOpen ? 'open-issue-card' : 'closed-issue-card'
                            }

                            let sensorResult = {
                                id: sensor.id,
                                name: sensor.name,
                                data: { ref: sensor.values[index] },
                                timestamp: timestamp,
                                issue_id: sensor.values[index].issue ? sensor.values[index].issue.issue_id : false,
                                checklist_id: sensor.values[index].issue ? sensor.values[index].issue.id : false,
                                status: sensor.values[index].issue ? sensor.values[index].issue.status : false,
                                type: hasIssue ? 'issue' : 'alert', // Distinguish between incidents and alerts
                                cardClass: cardClass
                            }
                            aSensor.push(sensorResult)
                        }
                    }
                })
                // Sort notifications: incidents first, then alerts, then by timestamp (newest first)
                aSensor.sort((a, b) => {
                    // Priority 1: Type (issues/incidents first)
                    if (a.type !== b.type) {
                        return a.type === 'issue' ? -1 : 1
                    }
                    // Priority 2: If same type, sort by timestamp (newest first)
                    return b.timestamp - a.timestamp
                })
                return aSensor
            }
            return []
        },
        notificationKpiData() {
            const s = this.$store.getters['sensor/getSensorStatus']()
            const issues = Number(s && s.issues) || 0
            const alerts = Number(s && s.alerts) || 0
            return { issues, alerts }
        },
        filteredNotifications() {
            const allNotifications = this.sensorsWithAlerts
            if (!allNotifications || allNotifications.length === 0) {
                return []
            }

            let filtered = allNotifications

            // Apply filter based on notificationFilter
            if (this.notificationFilter === 'issues') {
                filtered = allNotifications.filter((n) => n.type === 'issue')
            } else if (this.notificationFilter === 'alerts') {
                filtered = allNotifications.filter((n) => n.type === 'alert')
            }

            const byTimeDesc = (a, b) => {
                return Number(b.timestamp) - Number(a.timestamp)
            }

            let result
            if (!this.notificationFilter) {
                const issues = filtered.filter((n) => n.type === 'issue').sort(byTimeDesc)
                const alerts = filtered.filter((n) => n.type === 'alert').sort(byTimeDesc)
                result = [...issues, ...alerts]
            } else {
                result = filtered.slice().sort(byTimeDesc)
            }

            this.total = result.length

            // Return all filtered notifications (no pagination)
            return result
        },
        formattedNotifications() {
            return this.filteredNotifications.map((n) => ({
                sensorId: String(n.id),
                timestamp: String(n.timestamp),
                count: 0
            }))
        },
        totalPages() {
            return Math.ceil(this.total / this.perPage)
        },
        hasNextPage() {
            return this.perPage * this.page < this.total
        },
        kpiData() {
            return this.$store.getters['sensor/getSensorStatus']()
        },
        notificationCount() {
            return this.$store.getters['sensor/getAlertCounter'] + this.$store.getters['sensor/getIssueCounter']
        },
        prioritySensors() {
            let list = this.sensors.slice()

            if (this.activeKpiType) {
                if (this.activeKpiType === 'issues') {
                    // Issues abiertas
                    list = list.filter((s) => s && s.status !== 1 && this.hasOpenIssue(s))
                } else if (this.activeKpiType === 'alerts') {
                    // Alerts sin issues
                    list = list.filter((s) => (this.hasAlert(s)) && !this.hasAnyIssue(s))
                } else if (this.activeKpiType === 'disconnected') {
                    // Status = 1
                    list = list.filter((s) => s && s.status === 1)
                } else if (this.activeKpiType === 'connected') {
                    // Last value = OK
                    list = list.filter((s) => s && s.status !== 1 && !this.hasCurrentAlert(s) && s.config && s.config.alert == true)
                } else if (this.activeKpiType === 'offline') {
                    // Silenciados (alertas desactivadas), incluye los que también están sin datos
                    list = list.filter(isMutedSensor)
                }
            }

            return list.sort((a, b) => {
                const pa = this.priorityScore(a)
                const pb = this.priorityScore(b)

                if (pa !== pb) return pa - pb

                if (pa === 0) {
                    const aCurrent = this.hasCurrentAlert(a)
                    const bCurrent = this.hasCurrentAlert(b)

                    if (aCurrent !== bCurrent) return aCurrent ? -1 : 1

                    if (aCurrent && bCurrent) {
                        const sa = this.computeSeverity(a)
                        const sb = this.computeSeverity(b)
                        if (sa !== sb) return sb - sa
                    }

                    const aDate = a && a.last_value && a.last_value.date ? a.last_value.date : 0
                    const bDate = b && b.last_value && b.last_value.date ? b.last_value.date : 0
                    return bDate - aDate
                }

                return 0
            })
        }
    },
    methods: {
        handleKpiClick(type) {
            this.activeKpiType = this.activeKpiType === type ? null : type
        },
        checkKpiFromQuery() {
            const kpiType = this.$route.query.kpi
            if (kpiType) {
                const validKpiTypes = ['issues', 'alerts', 'offline', 'disconnected', 'connected']
                if (validKpiTypes.includes(kpiType)) {
                    this.activeKpiType = kpiType
                    const newQuery = { ...this.$route.query }
                    delete newQuery.kpi
                    this.$router.replace({ query: newQuery }).catch(() => {})
                }
            }
        },
        handleNotificationKpiClick(type) {
            // Toggle filter on/off
            if (this.notificationFilter === type) {
                this.notificationFilter = null // Deselect to show all
            } else {
                this.notificationFilter = type // Select to show only this type
            }
            // Reset to page 1 when filter changes
            this.page = 1
        },
        switchTab(tab) {
            this.activeTab = tab
            if (tab === 'notifications') {
                this.notificationFilter = null // Reset filter when switching to notifications
                this.page = 1 // Reset pagination
                this.openIssues('tabTwo')
            }
        },
        showCharts(param) {
            if (param) {
                this.sensorCharts = true
            } else {
                this.openIssues()
                this.sensorCharts = false
            }
        },
        calcDate() {
            this.currentFilters.date && this.currentFilters.date.start_date ? (this.maxDate = moment(this.currentFilters.date.start_date).format('YYYY-MM-DD')) : true
            this.currentFilters.date && this.currentFilters.date.end_date ? (this.minDate = moment(this.currentFilters.date.end_date).format('YYYY-MM-DD')) : true
            this.currentFilters = { ...this.currentFilters }
        },
        load() {
            let self = this
            this.$overlay.loading()
            let date = this.currentFilters
            let params = {
                start_date: date.type_date != 'custom' ? date.type_date : date.date.start_date,
                end_date: date.type_date == 'custom' ? date.date.end_date : false
            }
            this.conection = fnCheckConnection()
            this.$store.dispatch('sensor/loadList').then(() => {
                this.$store.dispatch('sensor/loadSensorValues', params).then(() => {
                    if (self.$route.params.id) {
                        self.sensorSelected = self.$store.getters['sensor/getSensorValues'](self.$route.params.id).id
                    }
                    self.$overlay.hide()
                    // Set isSensorsLoaded to true after data is loaded (for both API and mock data)
                    self.isSensorsLoaded = true
                })
            })
        },
        openIssues(prop) {
            let self = this
            if (prop == 'tabTwo') {
                this.$overlay.loading()
                let date = this.currentFilters
                let params = {
                    start_date: date.type_date != 'custom' ? date.type_date : date.date.start_date,
                    end_date: date.type_date == 'custom' ? date.date.end_date : false
                }
                if (Object.values(this.openIssuesList).length == 0) {
                    this.$store.dispatch('sensor/loadSensorOpenIssues', params).then((response) => {
                        self.openIssuesList = response
                        self.allIssuesSelected = false
                        self.numberOpenIssues = Object.values(response).length
                        self.$overlay.hide()
                    })
                } else {
                    self.allIssuesSelected = false
                    self.numberOpenIssues = Object.values(self.openIssuesList).length
                    self.$overlay.hide()
                }
            } else {
                self.allIssuesSelected = true
                self.$forceUpdate()
            }
        },
        back(filters) {
            this.sensorSelected = false
            this.$router.push({ name: 'Sensor' })
            if (JSON.stringify(filters) !== JSON.stringify(this.currentFilters)) {
                this.filterByDate(this.currentFilters)
            }
            this.printCharts = true
        },

        openIssuesPageNext() {
            this.pageOpenIssues++
            this.openIssuesPaginated = Object.values(this.openIssuesList).slice((this.pageOpenIssues - 1) * this.perPageOpenIssues, this.pageOpenIssues * this.perPageOpenIssues)
        },
        openIssuesPagePrev() {
            this.pageOpenIssues--
            this.openIssuesPaginated = Object.values(this.openIssuesList).slice((this.pageOpenIssues - 1) * this.perPageOpenIssues, this.pageOpenIssues * this.perPageOpenIssues)
        },
        selectSensor(sensor) {
            this.sensorSelected = sensor.id
            this.$router.push({
                name: 'SensorDetail',
                params: {
                    id: sensor.id
                }
            })
        },
        selectSensorFromNotification(sensorId) {
            // Handle notification card click - navigate to sensor detail
            this.sensorSelected = sensorId
            this.$router
                .push({
                    name: 'SensorDetail',
                    params: {
                        id: sensorId
                    }
                })
                .catch((err) => {
                    // Handle navigation duplicate error gracefully
                    if (err.name !== 'NavigationDuplicated') {
                        console.error(err)
                    }
                })
        },
        filterDetail(detail) {
            const self = this;
            let date = detail.filters
            let params = {
                id: detail.id,
                start_date: date.type_date != 'custom' ? date.type_date : date.date.start_date,
                end_date: date.type_date == 'custom' ? date.date.end_date : false
            }
            this.$store.dispatch('sensor/loadSensorValuesDetail', params).then(() => {
                self.$overlay.hide()
            })
        },
        filterByDate(date) {
            let self = this

            let params = {
                start_date: date.type_date != 'custom' ? date.type_date : date.date.start_date,
                end_date: date.type_date == 'custom' ? date.date.end_date : false
            }
            this.emptyFilters = { ...this.currentFilters }
            this.$store.dispatch('sensor/loadSensorValues', params).then(() => {
                if (!self.sensorCharts) {
                    self.$store.dispatch('sensor/loadSensorOpenIssues', params).then((response) => {
                        self.openIssuesList = response
                        self.numberOpenIssues = Object.values(response).length
                        self.sensorsWithAlerts
                    })
                }
            })
        },
        priorityScore(sensor) {
            if (sensor.status !== 1 && this.hasOpenIssue(sensor)) return 0
            if (sensor.status !== 1 && this.hasCurrentAlert(sensor)) return 1
            if (sensor.status !== 1 && this.hasAlert(sensor)) return 2
            if (sensor.status === 1) return 4
            if (!sensor.config || sensor.config.alert === false) return 3
            return 5
        },
        hasOpenIssue(sensor) {
            const values = this.$store.getters['sensor/getTestoSensorsInfo'](sensor.id)
            if (!values) return false
            const readings = Object.values(values.issuesList)
            return readings.some((r) => r && r.issue && this.issueStates[r.issue.status].type !== 3)
        },
        hasAnyIssue(sensor) {
            const values = this.$store.getters['sensor/getTestoSensorsInfo'](sensor.id)
            if (!values) return false
            return Array.isArray(values.issuesList) && values.issuesList.length > 0
        },
        hasCurrentAlert(sensor) {
            if (sensor.status === 1) return false
            const current = sensor.last_value && sensor.last_value.value
			const cond = sensor.config && sensor.config.condition
			const rawVal = sensor.config && sensor.config.value

			if (cond === '<') {
				const thr = Number(rawVal)
				return !isNaN(thr) ? current < thr : false
			}
			if (cond === '>') {
				const thr = Number(rawVal)
				return !isNaN(thr) ? current > thr : false
			}
			if (cond === '<>' || cond === '!<>') {
				if (typeof rawVal === 'string' && rawVal.includes(',')) {
					const parts = rawVal.split(',').map(v => parseFloat(String(v).trim()))
					const min = parts[0]
					const max = parts[1]
					if (isNaN(min) || isNaN(max)) return false
					const inside = current >= min && current <= max
					return cond === '<>' ? inside : !inside
				}
				return false
			}
            return false
        },
        hasAlert(sensor) {
            if (sensor.status === 1) return false
            if (this.hasOpenIssue(sensor)) return false

            const values = this.$store.getters['sensor/getTestoSensorsInfo'](sensor.id)
            if (values && values.alertsList > 0) return true

            if (!sensor.values) return false
            return Object.values(sensor.values).some((v) => v && v.alert === true)
        },
        isIssueOpen(encStatus) {
            const states = this.issueStates
            return !!(states && encStatus && states[encStatus] && states[encStatus].type !== 3)
        },
        computeSeverity(sensor) {
            if (!sensor || !sensor.config || !this.hasCurrentAlert(sensor)) return 0
			const current = Number(sensor.last_value && sensor.last_value.value)
			const cond = sensor.config.condition
			const rawVal = sensor.config.value

            if (cond === '<') {
                const thr = Number(rawVal)
                return isNaN(thr) ? 0 : Math.max(0, thr - current)
            }

            if (cond === '>') {
                const thr = Number(rawVal)
                return isNaN(thr) ? 0 : Math.max(0, current - thr)
            }

			if (cond === '!<>') {
                if (typeof rawVal === 'string' && rawVal.includes(',')) {
                    const parts = rawVal.split(',').map((v) => parseFloat(String(v).trim()))
                    const min = parts[0]
                    const max = parts[1]
                    if (isNaN(min) || isNaN(max)) return 0
                    if (current < min) return min - current
                    if (current > max) return current - max
                    return 0
                }
                const thr = Number(rawVal)
                if (isNaN(thr)) return 0
                return Math.abs(current - thr)
            }

			if (cond === '<>') {
				if (typeof rawVal === 'string' && rawVal.includes(',')) {
					const parts = rawVal.split(',').map((v) => parseFloat(String(v).trim()))
					const min = parts[0]
					const max = parts[1]
					if (isNaN(min) || isNaN(max)) return 0
					if (current >= min && current <= max) {
						return Math.min(current - min, max - current)
					}
					return 0
				}
				return 0
			}

            return 0
        }
    },
    created() {
        this.load()
    },
    mounted() {
        if (!this.$route.params.id) {
            this.printCharts = true
        }
        this.conection = fnCheckConnection()
        this.checkKpiFromQuery()
    },
    watch: {
        $route(to, from) {
            // Reset sensorSelected when navigating back to main sensor view
            if (to.name === 'Sensor' && !to.params.id) {
                this.sensorSelected = false
                this.checkKpiFromQuery()
            } else if (to.name === 'SensorDetail' && to.params.id) {
                this.sensorSelected = to.params.id
            }
        },
        openIssuesList(newValue, oldValue) {
            if (newValue) {
                this.openIssuesPaginated = Object.values(newValue).slice((this.pageOpenIssues - 1) * this.perPageOpenIssues, this.pageOpenIssues * this.perPageOpenIssues)
                this.totalOpenIssues = Object.values(newValue).length
            }
        },
        currentFilters: {
            deep: true,
            handler: function (newValue, oldValue) {
                if (JSON.stringify(newValue) !== JSON.stringify(this.emptyFilters)) {
                    this.showButtonFilter = true
                } else {
                    this.showButtonFilter = false
                }
            }
        },
        emptyFilters: {
            deep: true,
            handler: function (newValue, oldValue) {
                if (JSON.stringify(newValue) !== JSON.stringify(this.currentFilters)) {
                    this.showButtonFilter = true
                } else {
                    this.showButtonFilter = false
                }
            }
        }
    }
}
</script>

<style lang="scss" scoped>
.sensors:not(.detail) {
    width: 100%;
    height: 100%;
    padding: 16px;
    background-color: #f4f2ed;
    @include display-flex();
    @include flex-direction(column);
    overflow: hidden;

    // Header wrapper
    .header-wrapper {
        flex-shrink: 0;
        .header-top {
            @include display-flex();
            @include justify-content(space-between);
            @include align-items(center);
            position: relative;
        }

        .tabs-container {
            @include display-flex();
            align-items: center;
            gap: 8px;
            background-color: $neutro-light;
            @include border-radius(4px);
            padding: 4px;
            flex: 0 1 auto;

            .tab {
                @include display-flex();
                @include align-items(center);
                gap: 8px;
                padding: 8px;
                @include border-radius(4px);

                .i-andy-receptor {
                    color: #273151;
                }

                .i-andy-circle-exclamation {
                    color: $error-dark;
                }

                .tab-text {
                    font-size: 16px;
                    color: $neutro-s80;
                    font-family: $text-sbold;
                    font-weight: 700;
                    white-space: nowrap;
                }

                &.active {
                    background-color: #d7dae3;

                    .tab-text {
                        color: $main-dark;
                        font-family: $text-bold;
                    }
                }
            }

            .tab:not(.active):hover {
                background-color: $color-neutral-100;
                cursor: pointer;
            }
        }

        .header-right {
            flex: 0 1 auto;

            .refresh-button {
                @include display-flex();
                @include align-items(center);
                gap: 8px;
                padding: 9px 8px;
                background-color: $neutro-light;
                @include border-radius(8px);
                cursor: pointer;

                .i-andy-refresh-2 {
                    color: #b3b3b3;
                }

                .refresh-text {
                    @include font-size(m);
                    color: $neutro-s80;
                    font-family: $text-sbold;
                    font-weight: 600;
                }
            }
        }

        // Subtitle
        .subtitle {
            position: absolute;
            left: 50%;
            transform: translateX(-50%);
            color: $neutro-s80;
            font-size: 16px;
            font-style: normal;
            font-weight: 600;
            line-height: 20px;
            font-family: $text-sbold;
            white-space: nowrap;
        }
    }

    // Mobile/Portrait responsive styles
    @include media('<=tablet') {
        .header-wrapper {
            .tabs-container {
                padding: 4px;
                gap: 4px;

                .tab {
                    padding: 8px;
                    gap: 4px;

                    .tab-text {
                        display: none;
                    }
                }
            }

            .header-right {
                .refresh-button {
                    padding: 8px;

                    .refresh-text {
                        display: none;
                    }
                }
            }

            .subtitle {
                font-size: 14px;
                line-height: 18px;
            }
        }
    }

    ::v-deep(.sensors-content) {
        flex: 1;
        min-height: 0;
    }
    ::v-deep(.notifications-content) {
        flex: 1;
        min-height: 0;
    }
}

.sensors.detail {
    width: 100%;
    height: 100%;
    padding: unset;
    overflow: hidden;
}
</style>
